// frontend/src/utils/parameterSearchUtils.js
// Utility functions for parameter search functionality

import { PARAMETERS_MD_PARAMETERS } from './parameterAudit';
import { getParameterDisplayName } from './parameterDisplayNames';
import { biometricCategories } from './biometricParameters';
import { lifestyleCategories } from './lifestyleCategories';
import { imagingParameters } from './imagingParameters';
import { getAllLifestyleParameters } from './lifestyleDisplayMapping';
import i18n from '../i18n';

/**
 * Helper function to get user-friendly subcategory name for biometrics
 */
function getBiometricSubcategoryTitle(subcategoryKey) {
  const category = biometricCategories.find(cat => cat.key === subcategoryKey);
  if (category) {
    // Try to get translation, fallback to original title
    const translationKey = `categories.${subcategoryKey}`;
    const translated = i18n.t(translationKey);
    return translated !== translationKey ? translated : category.title;
  }
  return subcategoryKey?.charAt(0).toUpperCase() + subcategoryKey?.slice(1);
}

/**
 * Helper function to get user-friendly subcategory name for lifestyle
 */
function getLifestyleSubcategoryTitle(subcategoryKey) {
  const category = lifestyleCategories.find(cat => cat.key === subcategoryKey);
  if (category) {
    // Try to get translation, fallback to original title
    const translationKey = `categories.${subcategoryKey}`;
    const translated = i18n.t(translationKey);
    return translated !== translationKey ? translated : category.title;
  }
  return subcategoryKey?.charAt(0).toUpperCase() + subcategoryKey?.slice(1);
}

/**
 * Helper function to get user-friendly subcategory name for imaging
 */
function getImagingSubcategoryTitle(subcategoryKey) {
  // Special case: "other" should always return "Other Imaging"
  if (subcategoryKey === 'other') {
    return 'Other Imaging';
  }

  // For imaging, the subcategory key is like "spleen_us", "liver_us", etc.
  // We need to find the corresponding study type in imagingParameters
  const studyTypes = Object.keys(imagingParameters);

  // Convert subcategory key to study type format
  // e.g., "spleen_us" -> "Spleen US", "chest_xr" -> "Chest XR"
  const studyType = studyTypes.find(type => {
    const normalizedType = type.toLowerCase().replace(/\s+/g, '_');
    return normalizedType === subcategoryKey;
  });

  if (studyType) {
    return studyType;
  }

  // Fallback: try to create a reasonable display name from the key
  // e.g., "spleen_us" -> "Spleen US", "chest_xr" -> "Chest XR"
  const parts = subcategoryKey.split('_');
  if (parts.length >= 2) {
    const organOrStudy = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
    const modality = parts[1].toUpperCase();
    return `${organOrStudy} ${modality}`;
  }

  return subcategoryKey?.charAt(0).toUpperCase() + subcategoryKey?.slice(1);
}

/**
 * Build comprehensive parameter registry from parameters.md and existing mappings
 * This creates a searchable index of all parameters with their metadata
 */
export function buildParameterRegistry() {
  const registry = [];

  // Use the complete parameters.md list as the authoritative source
  let fallbackCount = 0;

  // Build a lookup map for lifestyle parameter types
  const lifestyleParameterTypeLookup = {};
  const allLifestyleParams = getAllLifestyleParameters();
  allLifestyleParams.forEach(param => {
    lifestyleParameterTypeLookup[param.key] = param.type;
  });

  PARAMETERS_MD_PARAMETERS.forEach((paramKey, index) => {
    // Get display name from centralized display names utility
    let displayName;
    try {
      displayName = getParameterDisplayName(paramKey);
    } catch (error) {
      // Fallback to parameter key if display name not found
      fallbackCount++;
      displayName = paramKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }

    // Determine category and subcategory from parameter key
    const parts = paramKey.split('_');
    const categoryPrefix = parts[0];
    const subcategoryPrefix = parts[1];

    let category, subcategory, type;

    switch (categoryPrefix) {
      case 'vitals':
        category = i18n.t('categories.vitals', 'Vitals');
        subcategory = null;
        type = 'Vital';
        break;
      case 'labs':
        category = i18n.t('categories.labResults', 'Lab Results');  // Changed from 'Labs' to match flowsheet expectations
        // Map subcategory prefixes to proper display names with translations
        const labSubcategoryMap = {
          'cbc': i18n.t('categories.cbc', 'CBC'),
          'lipid': i18n.t('categories.lipid', 'Lipid'),
          'glucose': i18n.t('categories.glucose', 'Glucose'),
          'hepatology': i18n.t('categories.hepatology', 'Hepatology'),
          'renal': i18n.t('categories.renal', 'Renal'),
          'electrolytes': i18n.t('categories.electrolytes', 'Electrolytes'),
          'thyroid': i18n.t('categories.thyroid', 'Thyroid'),
          'urine': i18n.t('categories.urine', 'Urine'),
          'coagulation': i18n.t('categories.coagulation', 'Coagulation'),
          'cardiac': i18n.t('categories.cardiac', 'Cardiac'),
          'protein': i18n.t('categories.protein', 'Protein'),
          'vitamins': i18n.t('categories.vitamins', 'Vitamins'),
          'inflammation': i18n.t('categories.inflammation', 'Inflammation')
        };
        subcategory = labSubcategoryMap[subcategoryPrefix] || subcategoryPrefix?.charAt(0).toUpperCase() + subcategoryPrefix?.slice(1);
        type = 'Lab';
        break;
      case 'biometrics':
        category = i18n.t('categories.biometrics', 'Biometrics');
        subcategory = getBiometricSubcategoryTitle(subcategoryPrefix);
        type = 'Biometric';
        break;
      case 'pe':
        category = i18n.t('categories.physicalExam', 'Physical Exam');
        // Hardcoded mapping based on actual physicalExamSectionsFull section titles with translations
        const peSubcategoryMap = {
          'integumentary': i18n.t('categories.integumentary', 'Integumentary'),
          'lymph': i18n.t('categories.lymphNodes', 'Lymph Nodes'),
          'heent': i18n.t('categories.heentEyes', 'HEENT - Eyes'), // Default for heent prefix
          'neck': i18n.t('categories.neck', 'Neck'),
          'chest': i18n.t('categories.chestRespiratory', 'Chest/Respiratory'),
          'cardio': i18n.t('categories.cardiovascular', 'Cardiovascular'),
          'abdomen': i18n.t('categories.abdomen', 'Abdomen'),
          'msk': i18n.t('categories.musculoskeletal', 'Musculoskeletal (MSK)'),
          'neuro': i18n.t('categories.neurological', 'Neurological')
        };

        // For more specific HEENT subcategories, check the third part
        if (subcategoryPrefix === 'heent' && parts.length >= 3) {
          const heentSubcategory = parts[2];
          const heentMap = {
            'eyes': i18n.t('categories.heentEyes', 'HEENT - Eyes'),
            'ears': i18n.t('categories.heentEars', 'HEENT - Ears'),
            'nose': i18n.t('categories.heentNose', 'HEENT - Nose/Sinus'),
            'mouth': i18n.t('categories.heentMouth', 'HEENT - Mouth/Throat'),
            'neck': i18n.t('categories.neck', 'Neck')
          };
          subcategory = heentMap[heentSubcategory] || i18n.t('categories.heentEyes', 'HEENT - Eyes');
        } else if (subcategoryPrefix === 'chest' && parts.length >= 3 && parts[2] === 'resp') {
          // Handle chest_resp -> 'Chest/Respiratory'
          subcategory = i18n.t('categories.chestRespiratory', 'Chest/Respiratory');
        } else {
          subcategory = peSubcategoryMap[subcategoryPrefix] || subcategoryPrefix?.charAt(0).toUpperCase() + subcategoryPrefix?.slice(1);
        }
        type = 'PhysicalExam';
        break;
      case 'lifestyle':
        category = i18n.t('categories.lifestyle', 'Lifestyle');
        subcategory = getLifestyleSubcategoryTitle(subcategoryPrefix);
        // Get the specific type (Questionnaire, BooleanCheckbox, etc.) from lifestyle categories
        type = lifestyleParameterTypeLookup[paramKey] || 'Lifestyle';
        break;
      case 'imaging':
        category = i18n.t('categories.imaging', 'Imaging');
        // For imaging, we need to extract the full subcategory (organ + modality)
        // e.g., imaging_spleen_us_craniocaudal_length -> subcategory should be "spleen_us"
        // Special case: "Other Imaging" parameters should all be grouped under "Other Imaging"
        if (subcategoryPrefix === 'other') {
          subcategory = 'Other Imaging';
        }
        // Special case: EKG parameters are just imaging_ekg_parameter, not imaging_ekg_modality_parameter
        else if (subcategoryPrefix === 'ekg') {
          subcategory = getImagingSubcategoryTitle('ekg');
        } else if (parts.length >= 3) {
          const organPart = parts[1];
          const modalityPart = parts[2];
          const fullSubcategory = `${organPart}_${modalityPart}`;
          subcategory = getImagingSubcategoryTitle(fullSubcategory);
        } else {
          subcategory = getImagingSubcategoryTitle(subcategoryPrefix);
        }
        type = 'Imaging';
        break;
      default:
        category = i18n.t('common.other', 'Other');
        subcategory = subcategoryPrefix;
        type = 'Parameter';
    }

    // Create search keywords
    const searchKeywords = [
      paramKey,
      displayName,
      category.toLowerCase(),
      subcategory?.toLowerCase(),
      ...displayName.toLowerCase().split(' ')
    ].filter(Boolean);

    registry.push({
      key: paramKey,
      displayName,
      category,
      subcategory,
      type,
      searchKeywords,
    });
  });

  // Count parameters by category
  const categoryBreakdown = {};
  registry.forEach(p => {
    categoryBreakdown[p.category] = (categoryBreakdown[p.category] || 0) + 1;
  });

  // Critical summary log only
  console.log(`[ParameterRegistry] ✅ Built ${registry.length} parameters | Categories: ${Object.keys(categoryBreakdown).length} | Fallbacks: ${fallbackCount}`, categoryBreakdown);

  return registry;
}

/**
 * Search parameters based on query string
 * @param {Array} registry - Parameter registry from buildParameterRegistry()
 * @param {string} query - Search query
 * @param {number} maxResults - Maximum number of results to return
 * @returns {Array} - Array of matching parameters
 */
export function searchParameters(registry, query, maxResults = 10) {
  if (!query || query.trim().length < 2) {
    return [];
  }

  const searchTerm = query.toLowerCase().trim();
  const results = [];

  registry.forEach(param => {
    let score = 0;

    // Safety checks for required properties
    if (!param.key || !param.displayName || !param.searchKeywords) {
      return;
    }

    // Exact match on key gets highest score
    if (param.key.toLowerCase() === searchTerm) {
      score = 100;
    }
    // Exact match on display name gets high score
    else if (param.displayName.toLowerCase() === searchTerm) {
      score = 90;
    }
    // Display name starts with search term
    else if (param.displayName.toLowerCase().startsWith(searchTerm)) {
      score = 80;
    }
    // Key contains search term
    else if (param.key.toLowerCase().includes(searchTerm)) {
      score = 70;
    }
    // Display name contains search term
    else if (param.displayName.toLowerCase().includes(searchTerm)) {
      score = 60;
    }
    // Category/subcategory matches
    else if (param.category && param.category.toLowerCase().includes(searchTerm) ||
             (param.subcategory && param.subcategory.toLowerCase().includes(searchTerm))) {
      score = 50;
    }
    // Any keyword matches - with additional safety check
    else if (param.searchKeywords && Array.isArray(param.searchKeywords) &&
             param.searchKeywords.some(keyword => keyword && typeof keyword === 'string' && keyword.toLowerCase().includes(searchTerm))) {
      score = 40;
    }

    if (score > 0) {
      results.push({ ...param, score });
    }
  });

  // Sort by score (highest first) and return top results
  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, maxResults);
}

/**
 * Get category key for navigation based on parameter
 * @param {Object} parameter - Parameter object from registry
 * @returns {string} - Category key for navigation
 */
export function getCategoryKeyForNavigation(parameter) {
  // Get translated category names for comparison
  const vitalsCategory = i18n.t('categories.vitals', 'Vitals');
  const labsCategory = i18n.t('categories.labResults', 'Lab Results');
  const biometricsCategory = i18n.t('categories.biometrics', 'Biometrics');
  const lifestyleCategory = i18n.t('categories.lifestyle', 'Lifestyle');
  const physicalExamCategory = i18n.t('categories.physicalExam', 'Physical Exam');
  const imagingCategory = i18n.t('categories.imaging', 'Imaging');

  switch (parameter.category) {
    case vitalsCategory:
    case 'Vitals':
      return 'Vitals';
    case labsCategory:
    case 'Labs':
    case 'Lab Results':
      // For lab parameters, navigate to the specific subcategory if available
      if (parameter.subcategory) {
        return parameter.subcategory; // e.g., 'CBC', 'Lipid', 'Glucose'
      }
      return 'Labs';
    case biometricsCategory:
    case 'Biometrics':
      // For biometrics, navigate to the specific subcategory using the raw key
      if (parameter.subcategory) {
        // Convert display name back to key for navigation
        const biometricCategory = biometricCategories.find(cat => cat.title === parameter.subcategory);
        return biometricCategory ? biometricCategory.key : parameter.subcategory.toLowerCase();
      }
      return 'Biometrics';
    case lifestyleCategory:
    case 'Lifestyle':
      // For lifestyle, navigate to the specific subcategory using the raw key
      if (parameter.subcategory) {
        // Convert display name back to key for navigation
        const lifestyleCategory = lifestyleCategories.find(cat => cat.title === parameter.subcategory);
        return lifestyleCategory ? lifestyleCategory.key : parameter.subcategory.toLowerCase();
      }
      return 'Lifestyle';
    case physicalExamCategory:
    case 'Physical Exam':
      // For physical exam, navigate to the specific section
      if (parameter.subcategory) {
        return parameter.subcategory; // e.g., 'Integumentary', 'Cardiovascular', 'HEENT - Eyes'
      }
      return 'Physical Exam';
    case imagingCategory:
    case 'Imaging':
      // For imaging, navigate to the specific study type
      if (parameter.subcategory) {
        // Convert display name to navigation key format
        return `Imaging_${parameter.subcategory.replace(/\s+/g, '')}`; // e.g., 'Imaging_SpleenUS', 'Imaging_LiverUS'
      }
      return 'Imaging';
    default:
      console.warn(`[ParameterRegistry] Unknown category: ${parameter.category}`);
      return 'All';
  }
}

/**
 * Format parameter for display in search results
 * @param {Object} parameter - Parameter object from registry
 * @returns {Object} - Formatted parameter for display
 */
export function formatParameterForDisplay(parameter) {
  // Extract standardized parameter name from DOM key
  let standardizedKey = parameter.key;

  // Parameters now use standardized keys directly - no prefixes to remove
  // Keep the standardized key as-is since we've updated the system to use parameters.md naming
  // Lab, Imaging, Physical Exam, and Biometric parameters all use standardized keys directly

  return {
    key: standardizedKey, // Show standardized parameter name from parameters.md
    label: parameter.displayName,
    category: parameter.category,
    subcategory: parameter.subcategory,
    description: `${parameter.category}${parameter.subcategory ? ` > ${parameter.subcategory}` : ''}`,
    units: parameter.units,
    options: parameter.options, // Include options for preset choices
    type: parameter.type, // Include parameter type
    // Use the standardized key for navigation since we've updated the system
    domKey: parameter.key,
  };
}
